import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { useTutorial } from "@/context/TutorialContext";
import { useFeatureFlagsContext } from "@/hooks/useFeatureFlag";

const OVERLAY_ZINDEX = 9990;
const SPOTLIGHT_PADDING = 8;
const TOOLTIP_WIDTH = 340;
const TOOLTIP_MARGIN = 16;
const TARGET_WAIT_MS = 4000;
const POLL_INTERVAL_MS = 120;

/**
 * Wait for an element matching `selector` to appear in the DOM.
 * Returns the element or null after `timeoutMs`.
 */
const waitForElement = (selector, timeoutMs = TARGET_WAIT_MS) =>
  new Promise((resolve) => {
    if (!selector) return resolve(null);
    const found = document.querySelector(selector);
    if (found) return resolve(found);

    let done = false;
    const finish = (el) => {
      if (done) return;
      done = true;
      observer.disconnect();
      clearInterval(pollId);
      clearTimeout(timeoutId);
      resolve(el);
    };
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) finish(el);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    // Fallback poll in case MutationObserver missed a swap.
    const pollId = setInterval(() => {
      const el = document.querySelector(selector);
      if (el) finish(el);
    }, POLL_INTERVAL_MS);
    const timeoutId = setTimeout(() => finish(null), timeoutMs);
  });

const computeTooltipPosition = (rect, placement) => {
  if (!rect) {
    return {
      left: `calc(50% - ${TOOLTIP_WIDTH / 2}px)`,
      top: "50%",
      transform: "translateY(-50%)",
    };
  }
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const centerX = rect.left + rect.width / 2;

  const desired =
    placement && placement !== "auto"
      ? placement
      : rect.top > vh / 2
      ? "top"
      : "bottom";

  let left = centerX - TOOLTIP_WIDTH / 2;
  let top;
  if (desired === "top") {
    top = rect.top - TOOLTIP_MARGIN;
    // Anchor bottom of tooltip near rect.top
    return clampTooltipRect(left, top, "bottom");
  }
  if (desired === "bottom") {
    top = rect.bottom + TOOLTIP_MARGIN;
    return clampTooltipRect(left, top, "top");
  }
  if (desired === "left") {
    left = rect.left - TOOLTIP_WIDTH - TOOLTIP_MARGIN;
    top = rect.top;
    return clampTooltipRect(left, top, "top");
  }
  // right
  left = rect.right + TOOLTIP_MARGIN;
  top = rect.top;
  return clampTooltipRect(left, top, "top");
};

const clampTooltipRect = (left, top, anchor) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 12;
  let l = Math.max(margin, Math.min(left, vw - TOOLTIP_WIDTH - margin));
  let t = top;
  if (anchor === "bottom") {
    // top param is the y of tooltip bottom; use transform
    t = Math.max(margin, top);
    return { left: `${l}px`, top: `${t}px`, transform: "translateY(-100%)" };
  }
  t = Math.max(margin, Math.min(top, vh - margin - 20));
  return { left: `${l}px`, top: `${t}px`, transform: "none" };
};

export default function TutorialOverlay() {
  const {
    activeTutorial,
    activeStep,
    currentStepIndex,
    isRunning,
    nextStep,
    prevStep,
    stop,
    skipTutorial,
  } = useTutorial();
  const { features, error: ffError } = useFeatureFlagsContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [targetRect, setTargetRect] = useState(null);
  const [targetMissing, setTargetMissing] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const runIdRef = useRef(0);

  const totalSteps = activeTutorial ? activeTutorial.steps.length : 0;

  // Determine whether current step is feature-gated OFF.
  const isFeatureGatedOff = useMemo(() => {
    if (!activeStep?.featureKey) return false;
    if (ffError) return false; // fail-open when flags API dead
    const f = features?.[activeStep.featureKey];
    return !(f && f.enabled);
  }, [activeStep, features, ffError]);

  // Auto-advance past feature-gated OFF steps
  useEffect(() => {
    if (!isRunning) return;
    if (isFeatureGatedOff) {
      const t = setTimeout(() => {
        nextStep();
      }, 0);
      return () => clearTimeout(t);
    }
  }, [isRunning, isFeatureGatedOff, nextStep]);

  // Step effect: navigate if needed, then wait for target element.
  useEffect(() => {
    if (!isRunning || !activeStep) {
      setTargetRect(null);
      setTargetMissing(false);
      setIsWaiting(false);
      return;
    }
    if (isFeatureGatedOff) return;
    const myRun = ++runIdRef.current;

    // Informational step — no target, center screen.
    if (activeStep.informational) {
      setTargetRect(null);
      setTargetMissing(false);
      setIsWaiting(false);
      return;
    }

    const desiredRoute = activeStep.route;
    if (desiredRoute && location.pathname !== desiredRoute) {
      setIsWaiting(true);
      setTargetRect(null);
      setTargetMissing(false);
      navigate(desiredRoute);
      // Wait for both route hop + element mount.
      return;
    }

    // On correct route → resolve target.
    if (!activeStep.target) {
      setTargetRect(null);
      setTargetMissing(true);
      setIsWaiting(false);
      return;
    }
    setIsWaiting(true);
    setTargetRect(null);
    setTargetMissing(false);

    waitForElement(activeStep.target).then((el) => {
      // Race guard.
      if (myRun !== runIdRef.current) return;
      setIsWaiting(false);
      if (!el) {
        setTargetMissing(true);
        setTargetRect(null);
        return;
      }
      setTargetMissing(false);
      updateRectFromElement(el);
    });
  }, [
    isRunning,
    activeStep,
    location.pathname,
    navigate,
    isFeatureGatedOff,
  ]);

  const updateRectFromElement = (el) => {
    try {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
    } catch (_e) { /* noop */ }
    // Wait a frame for scroll to settle before measuring.
    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top - SPOTLIGHT_PADDING,
        left: rect.left - SPOTLIGHT_PADDING,
        width: rect.width + SPOTLIGHT_PADDING * 2,
        height: rect.height + SPOTLIGHT_PADDING * 2,
      });
    });
  };

  // Re-measure on resize / scroll for a smoother highlight.
  useEffect(() => {
    if (!activeStep?.target || targetMissing) return;
    const handler = () => {
      const el = document.querySelector(activeStep.target);
      if (el) {
        const rect = el.getBoundingClientRect();
        setTargetRect({
          top: rect.top - SPOTLIGHT_PADDING,
          left: rect.left - SPOTLIGHT_PADDING,
          width: rect.width + SPOTLIGHT_PADDING * 2,
          height: rect.height + SPOTLIGHT_PADDING * 2,
        });
      }
    };
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [activeStep, targetMissing]);

  if (!isRunning || !activeStep || isFeatureGatedOff) return null;

  const isLastStep = currentStepIndex === totalSteps - 1;
  const showSpotlight = !!targetRect && !targetMissing && !activeStep.informational;
  const tooltipStyle = computeTooltipPosition(targetRect, activeStep.placement);

  return (
    <div
      className="fixed inset-0"
      style={{ zIndex: OVERLAY_ZINDEX, pointerEvents: "auto" }}
      data-testid="tutorial-overlay"
      data-tutorial-step={activeStep.id}
    >
      {/* Backdrop with optional cutout via 4-piece mask */}
      {showSpotlight ? (
        <SpotlightBackdrop rect={targetRect} />
      ) : (
        <div
          className="absolute inset-0"
          style={{ backgroundColor: "rgba(15,23,42,0.55)" }}
          data-testid="tutorial-backdrop-full"
        />
      )}

      {/* Tooltip card */}
      <div
        className="absolute bg-white rounded-2xl shadow-2xl border border-[#E5E5E5] p-5"
        style={{
          width: TOOLTIP_WIDTH,
          maxWidth: "calc(100vw - 24px)",
          ...tooltipStyle,
        }}
        role="dialog"
        aria-modal="true"
        aria-label={activeStep.title}
        data-testid="tutorial-tooltip"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold text-[#5C5C5C] uppercase tracking-wide">
              Langkah {currentStepIndex + 1} dari {totalSteps}
            </div>
            <h3
              className="font-display font-bold text-lg text-[#1A1A1A] mt-0.5"
              data-testid="tutorial-step-title"
            >
              {activeStep.title}
            </h3>
          </div>
          <button
            type="button"
            onClick={stop}
            aria-label="Tutup panduan"
            className="text-[#5C5C5C] hover:text-[#1A1A1A] p-1 rounded-lg"
            data-testid="tutorial-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        {isWaiting ? (
          <p className="text-sm text-[#5C5C5C]" data-testid="tutorial-step-loading">
            Membuka halaman…
          </p>
        ) : targetMissing ? (
          <div
            className="text-sm text-[#5C5C5C] bg-[#FAF9F6] border border-[#E5E5E5] rounded-xl p-3"
            data-testid="tutorial-target-missing"
          >
            {activeStep.body}
            <div className="mt-2 text-[11px] italic text-[#8A8A8A]">
              Bagian ini belum tersedia di halaman Anda. Lanjut ke langkah
              berikutnya.
            </div>
          </div>
        ) : (
          <p
            className="text-sm text-[#1A1A1A] leading-relaxed"
            data-testid="tutorial-step-body"
          >
            {activeStep.body}
          </p>
        )}

        {/* Progress bar */}
        <div className="mt-4 h-1.5 bg-[#F1F1F1] rounded-full overflow-hidden">
          <div
            className="h-full brand-btn-primary"
            style={{
              width: `${((currentStepIndex + 1) / totalSteps) * 100}%`,
              transition: "width 200ms ease",
            }}
            data-testid="tutorial-progress-fill"
          />
        </div>

        {/* Actions */}
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={skipTutorial}
            className="text-xs font-semibold text-[#5C5C5C] hover:text-[#1A1A1A] px-2 py-1"
            data-testid="tutorial-skip-btn"
          >
            Lewati Panduan
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStepIndex === 0}
              className="inline-flex items-center gap-1 px-3 h-9 rounded-xl border border-[#E5E5E5] text-sm font-semibold text-[#1A1A1A] hover:bg-[#FAF9F6] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="tutorial-prev-btn"
            >
              <ArrowLeft size={14} /> Kembali
            </button>
            <button
              type="button"
              onClick={nextStep}
              className="inline-flex items-center gap-1 px-4 h-9 rounded-xl brand-btn-primary text-sm font-bold"
              data-testid="tutorial-next-btn"
            >
              {isLastStep ? (
                <>
                  <CheckCircle2 size={14} /> Selesai
                </>
              ) : (
                <>
                  Lanjut <ArrowRight size={14} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Spotlight backdrop = 4 dim rectangles surrounding the target rect, leaving
 * the target itself un-dimmed. Avoids clip-path browser inconsistencies.
 */
function SpotlightBackdrop({ rect }) {
  const dim = "rgba(15,23,42,0.55)";
  const vw = typeof window !== "undefined" ? window.innerWidth : 0;
  const vh = typeof window !== "undefined" ? window.innerHeight : 0;
  const top = Math.max(0, rect.top);
  const left = Math.max(0, rect.left);
  const right = Math.min(vw, rect.left + rect.width);
  const bottom = Math.min(vh, rect.top + rect.height);
  const styleBase = { position: "absolute", backgroundColor: dim };

  return (
    <>
      {/* Top */}
      <div
        style={{ ...styleBase, top: 0, left: 0, right: 0, height: top }}
        data-testid="tutorial-backdrop-top"
      />
      {/* Bottom */}
      <div
        style={{
          ...styleBase,
          top: bottom,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />
      {/* Left */}
      <div
        style={{
          ...styleBase,
          top,
          left: 0,
          width: left,
          height: bottom - top,
        }}
      />
      {/* Right */}
      <div
        style={{
          ...styleBase,
          top,
          left: right,
          right: 0,
          height: bottom - top,
        }}
      />
      {/* Spotlight ring */}
      <div
        style={{
          position: "absolute",
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          border: "2px solid #E85D04",
          borderRadius: 12,
          boxShadow: "0 0 0 4px rgba(232,93,4,0.25)",
          pointerEvents: "none",
        }}
        data-testid="tutorial-spotlight"
      />
    </>
  );
}
